/**
 * Helpers for Finances → Payments «Mark as paid» / «Update Payment».
 * Avoids re-sending unchanged appliedto rows and recovers when the PUT
 * times out after the server already persisted the payment.
 */

export function paymentRefId(val) {
  if (val == null || val === "") return "";
  if (typeof val === "string" || typeof val === "number") return String(val);
  if (typeof val === "object") {
    if (val._id != null) return String(val._id);
    if (val.id != null) return String(val.id);
  }
  return "";
}

function yyyyMmDd(val) {
  if (val == null || val === "") return "";
  if (typeof val === "string") {
    const s = val.trim();
    if (!s) return "";
    if (s.includes("T")) return s.split("T")[0] || "";
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : s.slice(0, 10);
  }
  try {
    const d = new Date(val);
    if (!Number.isNaN(d.getTime())) return d.toISOString().split("T")[0];
  } catch (_) {
    /* ignore */
  }
  return "";
}

function normalizeAppliedRow(row) {
  if (!row || typeof row !== "object") return null;
  return {
    _id: paymentRefId(row._id) || null,
    expense: paymentRefId(row.expense) || "",
    amount: Number(row.amount) || 0,
    bank_commission: Number(row.bank_commission) || 0,
  };
}

export function normalizeAppliedToForCompare(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list
    .map(normalizeAppliedRow)
    .filter(Boolean)
    .sort((a, b) => {
      const ka = `${a._id || ""}|${a.expense}|${a.amount}|${a.bank_commission}`;
      const kb = `${b._id || ""}|${b.expense}|${b.amount}|${b.bank_commission}`;
      return ka.localeCompare(kb);
    });
}

export function appliedToRowsEqual(a, b) {
  return (
    JSON.stringify(normalizeAppliedToForCompare(a)) ===
    JSON.stringify(normalizeAppliedToForCompare(b))
  );
}

/**
 * Serialize changed fields for PUT /payment/:id.
 * - bankaccount / expense as ids
 * - appliedto only as lean rows (keeps _id when present → update, not create)
 * - partialUpdate when appliedto is not in the payload (skip full-doc validation noise)
 */
export function buildPaymentStatusUpdatePayload(changedFields = {}) {
  const payload = {};

  Object.keys(changedFields).forEach((key) => {
    const value = changedFields[key];
    if (key === "bankaccount") {
      const id = paymentRefId(value);
      if (id) payload.bankaccount = id;
      else if (value === null || value === "") payload.bankaccount = null;
      return;
    }
    if (key === "bank_account_to") {
      const id = paymentRefId(value);
      if (id) payload.bank_account_to = id;
      else if (value === null || value === "") payload.bank_account_to = null;
      return;
    }
    if (key === "appliedto") {
      const rows = Array.isArray(value) ? value : [];
      payload.appliedto = rows.map((row) => {
        const out = {
          expense: paymentRefId(row.expense) || null,
          amount: Number(row.amount) || 0,
          bank_commission: Number(row.bank_commission) || 0,
        };
        const id = paymentRefId(row._id);
        if (id) out._id = id;
        return out;
      });
      return;
    }
    payload[key] = value;
  });

  if (!Object.prototype.hasOwnProperty.call(payload, "appliedto")) {
    payload.partialUpdate = true;
  }

  return payload;
}

export function isPaymentUpdateTransportError(error) {
  if (!error) return false;
  if (error.response) return false;
  const code = String(error.code || "");
  if (code === "ERR_NETWORK" || code === "ECONNABORTED") return true;
  const msg = String(error.message || "").toLowerCase();
  return (
    msg.includes("network error") ||
    msg.includes("timeout") ||
    msg.includes("exceeded")
  );
}

/**
 * After a transport error, check whether the intended status was already saved.
 */
export async function verifyPaymentStatusPersisted(
  api,
  paymentId,
  intended = {}
) {
  if (!api || !paymentId) return false;
  try {
    const response = await api.get(
      `/api/gotrading/dynamic/payment/${paymentId}`,
      { timeout: 30000 }
    );
    const doc = response?.data?.data || response?.data;
    if (!doc || typeof doc !== "object") return false;

    if (Object.prototype.hasOwnProperty.call(intended, "paid")) {
      const wantPaid = !!intended.paid;
      const gotPaid = !!doc.paid;
      if (wantPaid !== gotPaid) return false;
    }
    if (Object.prototype.hasOwnProperty.call(intended, "payment_date")) {
      const want = yyyyMmDd(intended.payment_date);
      const got = yyyyMmDd(doc.payment_date);
      if (want && got && want !== got) return false;
      if (want && !got) return false;
    }
    if (Object.prototype.hasOwnProperty.call(intended, "bankaccount")) {
      const want = paymentRefId(intended.bankaccount);
      const got = paymentRefId(doc.bankaccount);
      if (want && got && want !== got) return false;
      if (want && !got) return false;
    }
    return true;
  } catch (_) {
    return false;
  }
}
